import { ActionUnion, ContractUnion } from "../types";
import { UploadSignedDocumentModal } from "./upload-signed-document-modal";
import { AlertDialog, useSnackbarsPipeline } from "@bonliva-crm/ui";
import {
  useNullifyConsultantBookingContractMutation,
  useNullifyConsultantContractMutation,
  usePublishConsultantBookingContractMutation,
  usePublishConsultantContractMutation,
  useUploadSignedConsultantContractMutation
} from "@bonliva-crm/graphql";
import { ConsultantEmailEditor } from "./email-editor";
import { AddSalaryChoiceModal } from "./add-salary-choice-modal";
import { useLocale } from "@hooks";
import { useState } from "react";
import { TextField } from "@material-ui/core";
import { ConsultantContractType } from "@bonliva-crm/shared";

export const ModalProvider = (props: {
  open: boolean;
  setOpen: (o: boolean) => void;
  action: ActionUnion;
  contracts: ContractUnion[];
  contract: ContractUnion;
  consultantId: string;
  handleDelete: (id: string) => void;
  customerContract?: boolean;
}) => {
  const { locale } = useLocale();

  const [
    publishConsultantContractMutation,
    { loading: publishConsultantContractLoading }
  ] = usePublishConsultantContractMutation();

  const [
    publishConsultantBookingContractMutation,
    { loading: publishBookingContractLoading }
  ] = usePublishConsultantBookingContractMutation();

  const [nullifyConsultantContractMutation] = useNullifyConsultantContractMutation();
  const [nullifyConsultantBookingContractMutation] =
    useNullifyConsultantBookingContractMutation();
  const [uploadConsultantContractMutation] = useUploadSignedConsultantContractMutation();

  const { showError, showSuccess } = useSnackbarsPipeline();

  const [nullificationReason, setNullificationReason] = useState("");

  const handleSendToOneflow = () => {
    if (props.customerContract) {
      publishConsultantBookingContractMutation({
        variables: {
          contractId: props.contract._id
        },
        onCompleted: (d) => {
          showSuccess(locale.Published);
          setTimeout(
            () => window.open(d.publishBookingContract.contractUrl, "_blank"),
            500
          );
          props.setOpen(false);
        },
        onError: (e) => {
          showError(locale.ErrorOccurred);
          console.error(e);
        }
      });
      return;
    }
    publishConsultantContractMutation({
      variables: {
        contractId: props.contract._id
      },
      onCompleted: (d) => {
        showSuccess(locale.Published);
        setTimeout(() => window.open(d.publishContract.contractUrl, "_blank"), 500);
        props.setOpen(false);
      },
      onError: (e) => {
        showError(locale.ErrorOccurred);
        console.error(e);
      }
    });
  };

  const handleNullify = (value: boolean, reason?: string) => {
    if (props.customerContract) {
      nullifyConsultantBookingContractMutation({
        variables: {
          contractId: props.contract._id,
          nullify: value
        },
        onCompleted: () => {
          showSuccess(value ? locale.Nullified : locale.UnNullified);
          props.setOpen(false);
        },
        onError: (e) => {
          showError(locale.ErrorOccurred);
          console.error(e);
        }
      });
      return;
    }
    nullifyConsultantContractMutation({
      variables: {
        contractId: props.contract._id,
        nullify: value,
        nullificationReason: reason || null
      },
      onCompleted: () => {
        showSuccess(value ? locale.Nullified : locale.UnNullified);
        props.setOpen(false);
      },
      onError: (e) => {
        showError(locale.ErrorOccurred);
        console.error(e);
      }
    });
  };

  const handleSign = () => {
    if (props.customerContract) {
      showError(locale.cannotPerformAction);
      props.setOpen(false);
      return;
    }
    uploadConsultantContractMutation({
      variables: {
        contractId: props.contract._id
      },
      onCompleted: () => {
        showSuccess(locale.Signed);
        props.setOpen(false);
      },
      onError: (e) => {
        showError(locale.ErrorOccurred);
        console.error(e);
      }
    });
  };

  switch (props.action) {
    case locale.UploadSignedDocument:
      return <UploadSignedDocumentModal {...props} />;
    case locale.SendToOneflow:
      return (
        <AlertDialog
          open={props.open}
          onOk={handleSendToOneflow}
          onClose={() => props.setOpen(false)}
          title={locale.SendToOneflow}
          loading={publishConsultantContractLoading || publishBookingContractLoading}
        >
          Detta kommer att skicka avtalet till Oneflow för signering. Är du säker på att
          du vill fortsätta?
        </AlertDialog>
      );
    case locale.RepublishToOneflow:
      return (
        <AlertDialog
          open={props.open}
          onOk={handleSendToOneflow}
          onClose={() => props.setOpen(false)}
          title={locale.RepublishToOneflow}
          loading={publishConsultantContractLoading || publishBookingContractLoading}
        >
          Detta kommer att återpublicera avtalet till Oneflow för signering. Är du säker
          på att du vill fortsätta?
        </AlertDialog>
      );
    case locale.Delete:
      return (
        <AlertDialog
          open={props.open}
          onOk={() => {
            props.handleDelete(props.contract._id);
            props.setOpen(false);
          }}
          onClose={() => props.setOpen(false)}
          title={locale.deleteContract}
        />
      );
    case locale.Nullify:
      return (
        <AlertDialog
          open={props.open}
          onOk={() => {
            handleNullify(true, nullificationReason || undefined);
            setNullificationReason("");
          }}
          onClose={() => {
            props.setOpen(false);
            setNullificationReason("");
          }}
          title={locale.Nullify}
        >
          {"Type" in props.contract &&
            props.contract.Type === ConsultantContractType.SelfInvoicing && (
              <TextField
                label={locale.NullificationReason}
                value={nullificationReason}
                onChange={(e) => setNullificationReason(e.target.value)}
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                style={{ marginTop: 16 }}
              />
            )}
        </AlertDialog>
      );
    case locale.UnNullify:
      return (
        <AlertDialog
          open={props.open}
          onOk={() => {
            handleNullify(false);
          }}
          onClose={() => props.setOpen(false)}
          title={locale.UnNullify}
        />
      );
    case locale.SendToConsultant:
      return <ConsultantEmailEditor {...props} />;
    case locale.SendToCustomer:
      return <ConsultantEmailEditor {...props} />;
    case locale.AddSalaryChoice:
      return <AddSalaryChoiceModal {...props} showNationalProcurement />;
    case locale.ApprovedByConsultant:
      return (
        <AlertDialog
          open={props.open}
          onOk={() => {
            handleSign();
          }}
          onClose={() => props.setOpen(false)}
          title={locale.ApprovedByConsultant}
        />
      );
    default:
      return undefined;
  }
};
